import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, Calculator, ChevronLeft, ChevronRight, Users, BookOpen, AlertCircle, FileText, ArrowLeft, Edit } from 'lucide-react';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import api from '../components/api';
import { formatDate, formatTime } from '../utils/formatUtils';

const CreateNoteExamenPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  
  const [session, setSession] = useState(null);
  const [examen, setExamen] = useState(null);
  const [etudiants, setEtudiants] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  useEffect(() => {
    loadSessionData();
  }, [sessionId]);

  const loadSessionData = async () => {
    try {
      setLoading(true);
      
      // Charger les données de la session d'examen
      const sessionResponse = await api.get(`/examen/sessions/${sessionId}/`);
      const sessionData = sessionResponse.data.data || sessionResponse.data;
      setSession(sessionData);
      
      // Charger les données de l'examen complet
      const examenId = sessionData.examen?.id || sessionData.examen;
      if (examenId) {
        const examenResponse = await api.get(`/examen/examens/${examenId}/`);
        const examenData = examenResponse.data.data || examenResponse.data;
        setExamen(examenData);
        
        // Charger les étudiants de la classe à partir des données de l'examen
        if (examenData.classe?.id) {
          const etudiantsResponse = await api.get(`/assiduite/etudiant-by-class/${examenData.classe.id}/`);
          setEtudiants(etudiantsResponse.data);
          
          // Charger les notes existantes pour cette session
          try {
            const notesResponse = await api.get(`/examen/sessions/${sessionId}/notes/`);
            
            // Créer un objet de notes indexé par étudiant ID
            const notesMap = {};
            const notesData = notesResponse.data.data || notesResponse.data;
            notesData.forEach(note => {
              notesMap[note.etudiant.id] = note;
            });

            // Créer la liste complète avec tous les étudiants
            const notesCompletes = etudiantsResponse.data.map(etudiant => {
              const existingNote = notesMap[etudiant.id];
              return {
                etudiant: etudiant,
                note: existingNote?.note || '',
                observation: existingNote?.observation || '',
                mention: existingNote?.mention || '',
                id: existingNote?.id || null
              };
            });

            setNotes(notesCompletes);
          } catch (notesError) {
            // Si pas de notes existantes, créer une liste vide
            const notesVides = etudiantsResponse.data.map(etudiant => ({
              etudiant: etudiant,
              note: '',
              observation: '',
              mention: '',
              id: null
            }));
            setNotes(notesVides);
          }
        } else {
          toast.error('Aucune classe associée à cet examen');
        }
      } else {
        toast.error('Impossible de charger les données de l\'examen');
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement:', error);
      if (error.response?.status === 404) {
        const url = error.config?.url;
        if (url?.includes('/sessions/')) {
          toast.error('Session d\'examen introuvable');
        } else if (url?.includes('/examens/')) {
          toast.error('Examen introuvable');
        } else if (url?.includes('/etudiant-by-class/')) {
          toast.error('Impossible de charger les étudiants de cette classe');
        } else {
          toast.error('Ressource introuvable');
        }
        navigate('/examens');
      } else if (error.response?.status === 401) {
        toast.error('Session expirée. Reconnectez-vous.');
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        toast.error('Problème de connexion réseau');
      } else {
        toast.error('Impossible de charger les données');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNoteChange = (index, field, value) => {
    setNotes(prev => {
      const newNotes = [...prev];
      const globalIndex = (currentPage - 1) * itemsPerPage + index;
      newNotes[globalIndex] = {
        ...newNotes[globalIndex],
        [field]: value
      };

      // Calculer automatiquement la mention basée sur la note
      if (field === 'note' && value !== '') {
        const noteValue = parseFloat(value);
        let mention = '';
        if (noteValue >= 16) mention = 'Très Bien';
        else if (noteValue >= 14) mention = 'Bien';
        else if (noteValue >= 12) mention = 'Assez Bien';
        else if (noteValue >= 10) mention = 'Passable';
        else if (noteValue >= 8) mention = 'Insuffisant';
        else mention = 'Très Insuffisant';
        
        newNotes[globalIndex].mention = mention;
      }

      return newNotes;
    });
  };

  const handleSave = async () => {
    // Validation : tous les étudiants doivent avoir une note
    const etudiantsSansNote = notes.filter(noteItem => 
      noteItem.note === '' || noteItem.note === null || noteItem.note === undefined
    );

    if (etudiantsSansNote.length > 0) {
      toast.error(`${etudiantsSansNote.length} étudiant(s) n'ont pas de note saisie. Veuillez compléter toutes les notes.`);
      return;
    }

    try {
      setSaving(true);
      
      // Préparer les données à envoyer (toutes les notes puisqu'elles sont obligatoires)
      const notesToSave = notes.map(noteItem => ({
        session_id: sessionId,
        etudiant_id: noteItem.etudiant.id,
        note: parseFloat(noteItem.note),
        observation: noteItem.observation,
        absent: false,
        triche: false,
        copie_rendue: true
      }));

      // Envoyer les notes en lot via l'endpoint bulk
      const response = await api.post(`/examen/sessions/${sessionId}/notes/bulk/`, {
        notes: notesToSave
      });

      toast.success(response.data.message || 'Notes sauvegardées avec succès');
      navigate(`/examens/${examen?.id || session?.examen?.id}`);
    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde:', error);
      if (error.response?.status === 400) {
        const errorData = error.response.data;
        if (errorData.details && Array.isArray(errorData.details)) {
          // Erreurs spécifiques pour certains étudiants
          const errorCount = errorData.details.length;
          toast.error(`${errorCount} erreur(s) de validation. Vérifiez les notes saisies.`);
        } else {
          toast.error(errorData.error || 'Données invalides. Vérifiez les notes saisies.');
        }
      } else if (error.response?.status === 401) {
        toast.error('Session expirée. Reconnectez-vous.');
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        toast.error('Problème de connexion réseau');
      } else {
        toast.error('Impossible de sauvegarder les notes');
      }
    } finally {
      setSaving(false);
    }
  };

  const calculateStats = () => {
    const validNotes = notes
      .filter(n => n.note !== '' && n.note !== null)
      .map(n => parseFloat(n.note));

    if (validNotes.length === 0) return null;

    const moyenne = validNotes.reduce((sum, note) => sum + note, 0) / validNotes.length;
    const min = Math.min(...validNotes);
    const max = Math.max(...validNotes);
    const reussite = validNotes.filter(note => note >= 10).length;

    return {
      moyenne: Math.round(moyenne * 100) / 100,
      min,
      max,
      reussite,
      total: validNotes.length,
      pourcentageReussite: Math.round((reussite / validNotes.length) * 100)
    };
  };

  // Pagination
  const totalPages = Math.ceil(notes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentNotes = notes.slice(startIndex, endIndex);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const stats = calculateStats();

  // Calcul des étudiants sans note pour la validation
  const etudiantsSansNote = notes.filter(noteItem => 
    noteItem.note === '' || noteItem.note === null || noteItem.note === undefined
  );

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#202654]"></div>
            <span className="text-gray-600 font-medium">Chargement...</span>
          </div>
        </div>
      </Layout>
    );
  }

  const handleBack = () => {
    if (examen?.id) {
      navigate(`/examens/${examen.id}`);
    } else {
      navigate('/examens');
    }
  };

  return (
    <Layout>
      {/* Bannière Header avec bouton retour */}
      <div className="mt-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white mx-4 rounded-lg">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBack}
                className="flex items-center justify-center w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                title="Retour aux détails de l'examen"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-white/10">
                  <Edit className="h-6 w-6" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold">Gestion des notes</h1>
                  <p className="text-indigo-100">
                    {examen?.libelle || session?.examen?.libelle} - {session?.module?.libelle || session?.module?.module?.libelle}
                  </p>
                </div>
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-4 text-sm">
              <div className="text-center">
                <div className="text-xl font-bold">{notes.length}</div>
                <div className="text-indigo-200">Étudiants</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold">{stats?.total || 0}</div>
                <div className="text-indigo-200">Notes saisies</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold">{etudiantsSansNote.length}</div>
                <div className="text-indigo-200">En attente</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Titre et informations */}
        <div className="mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Gestion des notes d'examen
                </h1>
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <div className="flex items-center">
                    <FileText className="h-4 w-4 mr-1" />
                    {examen?.libelle || session?.examen?.libelle}
                  </div>
                  <div className="flex items-center">
                    <BookOpen className="h-4 w-4 mr-1" />
                    {session?.module?.libelle || session?.module?.module?.libelle}
                  </div>
                  <div className="flex items-center">
                    <Users className="h-4 w-4 mr-1" />
                    {examen?.classe?.libelle || session?.examen?.classe?.libelle}
                  </div>
                  {session?.date_examen && (
                    <div>
                      {formatDate(session.date_examen)} - {formatTime(session.heure_debut)} à {formatTime(session.heure_fin)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Statistiques */}
        {stats && (
          <div className="mb-8">
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Calculator className="h-5 w-5 mr-2" />
                Statistiques
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg text-center border border-blue-200">
                  <div className="text-2xl font-bold text-blue-600">{stats.moyenne}/20</div>
                  <div className="text-sm text-blue-700">Moyenne générale</div>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg text-center border border-green-200">
                  <div className="text-2xl font-bold text-green-600">{stats.max}/20</div>
                  <div className="text-sm text-green-700">Note maximale</div>
                </div>
                <div className="bg-gradient-to-br from-red-50 to-red-100 p-4 rounded-lg text-center border border-red-200">
                  <div className="text-2xl font-bold text-red-600">{stats.min}/20</div>
                  <div className="text-sm text-red-700">Note minimale</div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg text-center border border-purple-200">
                  <div className="text-2xl font-bold text-purple-600">{stats.reussite}/{stats.total}</div>
                  <div className="text-sm text-purple-700">Étudiants admis</div>
                </div>
                <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-4 rounded-lg text-center border border-yellow-200">
                  <div className="text-2xl font-bold text-yellow-600">{stats.pourcentageReussite}%</div>
                  <div className="text-sm text-yellow-700">Taux de réussite</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Table des notes */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Liste des étudiants ({notes.length})
              </h2>
              <div className="text-sm text-gray-500">
                Page {currentPage} sur {totalPages}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Étudiant
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Note (/20)
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mention
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Observation
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentNotes.map((noteItem, index) => (
                  <tr key={noteItem.etudiant.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#202654] to-[#202654]/80 flex items-center justify-center">
                            <span className="text-sm font-medium text-white">
                              {noteItem.etudiant.nom.charAt(0)}{noteItem.etudiant.prenom.charAt(0)}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {noteItem.etudiant.nom} {noteItem.etudiant.prenom}
                          </div>
                          <div className="text-sm text-gray-500">
                            {noteItem.etudiant.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <input
                        type="number"
                        min="0"
                        max="20"
                        step="0.5"
                        value={noteItem.note}
                        onChange={(e) => handleNoteChange(index, 'note', e.target.value)}
                        className={`w-20 px-3 py-2 text-center border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202654] focus:border-transparent transition-all ${
                          noteItem.note === '' || noteItem.note === null || noteItem.note === undefined
                            ? 'border-red-300 bg-red-50'
                            : 'border-gray-300'
                        }`}
                        placeholder="Note"
                        required
                      />
                    </td>
                    <td className="px-6 py-4 text-center">
                      {noteItem.mention && (
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                          noteItem.mention === 'Très Bien' ? 'bg-green-100 text-green-800 border border-green-200' :
                          noteItem.mention === 'Bien' ? 'bg-blue-100 text-blue-800 border border-blue-200' :
                          noteItem.mention === 'Assez Bien' ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
                          noteItem.mention === 'Passable' ? 'bg-orange-100 text-orange-800 border border-orange-200' :
                          'bg-red-100 text-red-800 border border-red-200'
                        }`}>
                          {noteItem.mention}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <input
                        type="text"
                        value={noteItem.observation}
                        onChange={(e) => handleNoteChange(index, 'observation', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202654] focus:border-transparent transition-all"
                        placeholder="Ajouter une observation..."
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Affichage de {startIndex + 1} à {Math.min(endIndex, notes.length)} sur {notes.length} étudiants
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Précédent
                  </button>
                  
                  <div className="flex space-x-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        onClick={() => goToPage(page)}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                          currentPage === page
                            ? 'bg-[#202654] text-white shadow-sm'
                            : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                  
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    Suivant
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Bouton de sauvegarde en bas */}
        <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              {etudiantsSansNote.length > 0 && (
                <div className="flex items-center text-orange-600 text-sm">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {etudiantsSansNote.length} étudiant(s) sans note
                </div>
              )}
              {etudiantsSansNote.length === 0 && (
                <div className="flex items-center text-green-600 text-sm">
                  <Save className="h-4 w-4 mr-1" />
                  Toutes les notes sont complètes
                </div>
              )}
            </div>
            <button
              onClick={handleSave}
              disabled={saving || etudiantsSansNote.length > 0}
              className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-[#202654] hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sauvegarde en cours...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Sauvegarder les notes ({notes.length})
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CreateNoteExamenPage;
